import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';

const Gallery = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    {
      src: 'https://images.unsplash.com/photo-1683629357884-11440746a7ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwcmVub3ZhdGlvbiUyMHdoaXRlJTIwZ29sZCUyMG1hcmJsZXxlbnwwfHx8fDE3NzI3ODI4MDV8MA&ixlib=rb-4.1.0&q=85',
      alt: 'Modern Kitchen Renovation',
      category: 'Kitchen',
      span: 'col-span-2 row-span-2',
    },
    {
      src: 'https://images.unsplash.com/photo-1771929662486-f793e08f0f16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbSUyMHJlbm92YXRpb24lMjBzcGElMjBsaWtlfGVufDB8fHx8MTc3Mjc4MjgwNnww&ixlib=rb-4.1.0&q=85',
      alt: 'Spa-like Bathroom',
      category: 'Bathroom',
      span: 'col-span-1 row-span-1',
    },
    {
      src: 'https://images.unsplash.com/photo-1683629357935-f3f4777ddf41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwcmVub3ZhdGlvbiUyMHdoaXRlJTIwZ29sZCUyMG1hcmJsZXxlbnwwfHx8fDE3NzI3ODI4MDV8MA&ixlib=rb-4.1.0&q=85',
      alt: 'Kitchen Details',
      category: 'Kitchen',
      span: 'col-span-1 row-span-1',
    },
    {
      src: 'https://images.unsplash.com/photo-1771239048293-72abf673adb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbSUyMHJlbm92YXRpb24lMjBzcGElMjBsaWtlfGVufDB8fHx8MTc3Mjc4MjgwNnww&ixlib=rb-4.1.0&q=85',
      alt: 'Luxury Bathroom',
      category: 'Bathroom',
      span: 'col-span-1 row-span-2',
    },
    {
      src: 'https://images.unsplash.com/photo-1765766600805-e75c44124d2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbSUyMHJlbm92YXRpb24lMjBzcGElMjBsaWtlfGVufDB8fHx8MTc3Mjc4MjgwNnww&ixlib=rb-4.1.0&q=85',
      alt: 'Bathroom Vanity',
      category: 'Bathroom',
      span: 'col-span-1 row-span-1',
    },
    {
      src: 'https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaG9tZSUyMHJlbm92YXRpb24lMjBicmlnaHQlMjBlbGVnYW50fGVufDB8fHx8MTc3Mjc4MjgwN3ww&ixlib=rb-4.1.0&q=85',
      alt: 'Living Room Renovation',
      category: 'Living Room',
      span: 'col-span-2 row-span-1',
    },
  ];

  return (
    <>
      <section
        id="gallery"
        data-testid="gallery-section"
        ref={ref}
        className="section-padding bg-[#F4F4F5]"
      >
        <div className="container-custom">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              Our Portfolio
            </p>
            <h2
              data-testid="gallery-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-bold mb-6"
            >
              Project Gallery
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-[#71717A] max-w-2xl mx-auto">
              Browse through our collection of completed renovation projects
              showcasing modern kitchens, luxurious bathrooms, and stunning home
              transformations.
            </p>
          </motion.div>

          {/* Bento Grid Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]"
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`gallery-image-${index}`}
                onClick={() => setSelectedImage(image)}
                className={`relative overflow-hidden cursor-pointer group ${image.span}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                    <ZoomIn className="w-8 h-8 text-white mx-auto mb-2" />
                    <p className="text-white font-medium">{image.category}</p>
                  </div>
                </div>
                {/* Category Badge */}
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="bg-[#D4AF37] text-[#0A0A0A] text-xs font-medium px-3 py-1">
                    {image.category}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-white hover:text-[#D4AF37] transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl max-h-[85vh]">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="text-center mt-4">
              <p className="text-white font-serif text-xl">{selectedImage.alt}</p>
              <p className="text-[#D4AF37] text-sm">{selectedImage.category}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
